/**
 * DTOs para SIMCE
 * Estos enums coinciden con los del backend
 */

export enum NivelSimce {
  CUARTO_BASICO = "CUARTO_BASICO",
  SEXTO_BASICO = "SEXTO_BASICO",
  OCTAVO_BASICO = "OCTAVO_BASICO",
  SEGUNDO_MEDIO = "SEGUNDO_MEDIO",
}

export enum AsignaturaSimce {
  MATEMATICAS = "MATEMATICAS",
  LENGUAJE = "LENGUAJE",
  CIENCIAS = "CIENCIAS",
  HISTORIA = "HISTORIA",
  INGLES = "INGLES",
  EDUCACION_FISICA = "EDUCACION_FISICA",
}

export enum DificultadSimce {
  ELEMENTAL = "ELEMENTAL",
  BASICA = "BASICA",
  ADECUADA = "ADECUADA",
}

export enum TipoCreadorPreguntaSimce {
  PROFESOR = "PROFESOR",
  COLEGIO = "COLEGIO",
  PLATAFORMA = "PLATAFORMA",
  EXTERNO = "EXTERNO",
}

export interface AlternativaSimce {
  letra: "A" | "B" | "C" | "D";
  texto: string;
  esCorrecta: boolean;
}

export interface PreguntaSimce {
  idPreguntaSimce: number;
  contenido: string;
  asignatura: AsignaturaSimce;
  nivel: NivelSimce;
  dificultad: DificultadSimce;
  alternativas: AlternativaSimce[];
  explicacion: string;
  tiempoEstimado: number;
  ejeTematico?: string | null;
  habilidad?: string | null;
  revisada: boolean;
  tipoCreador: TipoCreadorPreguntaSimce;
  creadoPor: number | null;
  colegioId: number | null;
  autorNombre?: string | null;
  fuente?: string | null;
  fechaCreacion: string;
  fechaActualizacion: string;
  vigente: boolean;
  creador: {
    idUsuario: number;
    nombres: string;
    apellidos: string;
    email: string;
  } | null;
  colegio: {
    idColegio: number;
    nombre: string;
  } | null;
  puedeEliminar?: boolean;
}

export interface PruebaSimce {
  idPruebaSimce: number;
  nombre: string;
  descripcion: string | null;
  asignatura: AsignaturaSimce;
  nivel: NivelSimce;
  preguntasIds: number[];
  preguntas?: Array<{
    idPreguntaSimce: number;
    contenido: string;
    asignatura: string;
    nivel: string;
    dificultad: string;
    alternativas: AlternativaSimce[];
    explicacion: string;
    tiempoEstimado: number;
    ejeTematico?: string | null;
    habilidad?: string | null;
    orden: number;
  }>;
  tiempoTotal: number;
  creadoPor: number;
  fechaCreacion: string;
  fechaActualizacion: string;
  vigente: boolean;
  creador: {
    idUsuario: number;
    nombres: string;
    apellidos: string;
    email: string;
  } | null;
  puedeEliminar?: boolean;
}

export interface FiltrosPreguntasSimce {
  asignatura?: AsignaturaSimce;
  nivel?: NivelSimce;
  dificultad?: DificultadSimce;
  revisada?: boolean;
  tipoCreador?: TipoCreadorPreguntaSimce;
  soloMiColegio?: boolean;
  busqueda?: string;
  vigente?: boolean;
}

export interface CrearPreguntaSimceDTO {
  contenido: string;
  asignatura: AsignaturaSimce;
  nivel: NivelSimce;
  dificultad: DificultadSimce;
  alternativas: Omit<AlternativaSimce, "esCorrecta">[];
  respuestaCorrecta: "A" | "B" | "C" | "D";
  explicacion: string;
  tiempoEstimado: number;
  ejeTematico?: string;
  habilidad?: string;
  tipoCreador?: TipoCreadorPreguntaSimce;
  autorNombre?: string;
  fuente?: string;
}

export interface CrearPruebaSimceDTO {
  nombre: string;
  descripcion?: string;
  asignatura: AsignaturaSimce;
  nivel: NivelSimce;
  preguntasIds: number[];
}

export interface ActualizarPreguntaSimceDTO
  extends Partial<CrearPreguntaSimceDTO> {
  idPreguntaSimce: number;
}

export interface ActualizarPruebaSimceDTO extends Partial<CrearPruebaSimceDTO> {
  idPruebaSimce: number;
}

// Labels para mostrar en la UI
export const ASIGNATURAS_LABELS: Record<AsignaturaSimce, string> = {
  [AsignaturaSimce.MATEMATICAS]: "Matemáticas",
  [AsignaturaSimce.LENGUAJE]: "Lenguaje y Comunicación",
  [AsignaturaSimce.CIENCIAS]: "Ciencias Naturales",
  [AsignaturaSimce.HISTORIA]: "Historia, Geografía y Ciencias Sociales",
  [AsignaturaSimce.INGLES]: "Inglés",
  [AsignaturaSimce.EDUCACION_FISICA]: "Educación Física",
};

export const NIVELES_LABELS: Record<NivelSimce, string> = {
  [NivelSimce.CUARTO_BASICO]: "4° Básico",
  [NivelSimce.SEXTO_BASICO]: "6° Básico",
  [NivelSimce.OCTAVO_BASICO]: "8° Básico",
  [NivelSimce.SEGUNDO_MEDIO]: "2° Medio",
};

export const DIFICULTAD_LABELS: Record<DificultadSimce, string> = {
  [DificultadSimce.ELEMENTAL]: "Elemental",
  [DificultadSimce.BASICA]: "Básica",
  [DificultadSimce.ADECUADA]: "Adecuada",
};

export const DIFICULTAD_COLORS: Record<DificultadSimce, string> = {
  [DificultadSimce.ELEMENTAL]: "bg-green-100 text-green-800 border-green-200",
  [DificultadSimce.BASICA]: "bg-yellow-100 text-yellow-800 border-yellow-200",
  [DificultadSimce.ADECUADA]: "bg-red-100 text-red-800 border-red-200",
};

// Orden de dificultades según SIMCE (de menor a mayor dificultad)
export const DIFICULTADES_ORDENADAS: DificultadSimce[] = [
  DificultadSimce.BASICA,
  DificultadSimce.ELEMENTAL,
  DificultadSimce.ADECUADA,
];

// Enums y tipos para asignaciones
export enum EstadoAsignacionPrueba {
  PENDIENTE = "PENDIENTE",
  EN_CURSO = "EN_CURSO",
  COMPLETADA = "COMPLETADA",
  VENCIDA = "VENCIDA",
  CANCELADA = "CANCELADA",
}

export interface AsignacionPruebaSimce {
  idAsignacionPruebaSimce: number;
  pruebaSimceId: number;
  estudianteId: number;
  asignadoPor: number;
  fechaAsignacion: string;
  fechaLimite: string | null;
  estado: EstadoAsignacionPrueba;
  fechaActualizacion: string;
  prueba: {
    idPruebaSimce: number;
    nombre: string;
    descripcion: string | null;
    asignatura: AsignaturaSimce;
    nivel: NivelSimce;
    tiempoTotal: number;
  };
  estudiante: {
    idUsuario: number;
    nombres: string;
    apellidos: string;
    email: string;
    rut: string | null;
  };
  asignador: {
    idUsuario: number;
    nombres: string;
    apellidos: string;
    email: string;
  };
}

export interface AsignacionPruebaSimceDetalle extends AsignacionPruebaSimce {
  prueba: PruebaSimce;
  estudiante: {
    idUsuario: number;
    nombres: string;
    apellidos: string;
    email: string;
    rut: string | null;
    cursos: Array<{
      idCurso: number;
      nombre: string;
      anio: number;
      colegio: string | null;
    }>;
  };
}

export interface AsignarPruebaSimceDTO {
  idPruebaSimce: number;
  estudianteIds: number[];
  fechaLimite?: string | null;
}

export interface FiltrosAsignacionesPruebaSimce {
  pruebaSimceId?: number;
  estudianteId?: number;
  estado?: EstadoAsignacionPrueba;
}

export const ESTADO_ASIGNACION_LABELS: Record<EstadoAsignacionPrueba, string> =
  {
    [EstadoAsignacionPrueba.PENDIENTE]: "Pendiente",
    [EstadoAsignacionPrueba.EN_CURSO]: "En Curso",
    [EstadoAsignacionPrueba.COMPLETADA]: "Completada",
    [EstadoAsignacionPrueba.VENCIDA]: "Vencida",
    [EstadoAsignacionPrueba.CANCELADA]: "Cancelada",
  };

export const ESTADO_ASIGNACION_COLORS: Record<EstadoAsignacionPrueba, string> =
  {
    [EstadoAsignacionPrueba.PENDIENTE]: "bg-yellow-100 text-yellow-800",
    [EstadoAsignacionPrueba.EN_CURSO]: "bg-blue-100 text-blue-800",
    [EstadoAsignacionPrueba.COMPLETADA]: "bg-green-100 text-green-800",
    [EstadoAsignacionPrueba.VENCIDA]: "bg-red-100 text-red-800",
    [EstadoAsignacionPrueba.CANCELADA]: "bg-gray-100 text-gray-800",
  };

export const TIPO_CREADOR_LABELS: Record<TipoCreadorPreguntaSimce, string> = {
  [TipoCreadorPreguntaSimce.PROFESOR]: "Profesor",
  [TipoCreadorPreguntaSimce.COLEGIO]: "Colegio",
  [TipoCreadorPreguntaSimce.PLATAFORMA]: "Plataforma",
  [TipoCreadorPreguntaSimce.EXTERNO]: "Externo",
};
